/**
 * EditContactModal — Contact-Vollausbau
 *
 * Sektionen: Stammdaten | Wallets | Zahlung | Optionen
 * Scrollbares Modal (max-height 90vh), sticky Footer.
 * Pattern: key=contact.id sorgt fuer automatischen State-Reset bei neuem Contact.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateContact } from './useContactMutations'
import type { ContactFormData } from './useContactMutations'
import type { Contact } from './useContacts'
import type { ContactDetail } from './useContactDetail'

type ContactLike = Contact | ContactDetail

type Props = {
  contact: ContactLike
  isOpen: boolean
  onClose: () => void
}

type FormErrors = {
  name?: string
  email?: string
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

// --- Inner Form-Komponente (wird per key re-mounted bei neuem Contact) ---

function EditContactForm({
  contact,
  onClose,
}: {
  contact: ContactLike
  onClose: () => void
}) {
  const { t } = useTranslation('contacts')
  // Stammdaten
  const [name, setName] = useState(contact.name)
  const [email, setEmail] = useState(contact.email ?? '')
  const [phone, setPhone] = useState(contact.phone ?? '')
  const [address, setAddress] = useState(contact.address ?? '')
  const [notes, setNotes] = useState(contact.notes ?? '')

  // Wallets
  const [heliumWallet, setHeliumWallet] = useState(contact.helium_wallet ?? '')
  const [btcWallet, setBtcWallet] = useState(contact.btc_wallet ?? '')
  const [ethWallet, setEthWallet] = useState(contact.eth_wallet ?? '')
  const [usdtWallet, setUsdtWallet] = useState(contact.usdt_wallet ?? '')
  const [usdcWallet, setUsdcWallet] = useState(contact.usdc_wallet ?? '')

  // Zahlung
  const [currency, setCurrency] = useState(contact.currency ?? '')
  const [paymentMethod, setPaymentMethod] = useState(contact.payment_method ?? '')
  const [iban, setIban] = useState(contact.iban ?? '')
  const [paypal, setPaypal] = useState(contact.paypal ?? '')

  // Optionen
  const [monthlyReport, setMonthlyReport] = useState(contact.monthly_report)
  const [broadcastEnabled, setBroadcastEnabled] = useState(contact.broadcast_enabled)

  const [errors, setErrors] = useState<FormErrors>({})

  const updateContact = useUpdateContact()

  function validate(): boolean {
    const newErrors: FormErrors = {}

    if (!name.trim()) {
      newErrors.name = t('editModal.validation.nameRequired')
    }

    if (email && !validateEmail(email)) {
      newErrors.email = t('editModal.validation.emailInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    const data: ContactFormData = {
      name: name.trim(),
      email: email || null,
      phone: phone || null,
      address: address || null,
      notes: notes || null,
      helium_wallet: heliumWallet || null,
      btc_wallet: btcWallet || null,
      eth_wallet: ethWallet || null,
      usdt_wallet: usdtWallet || null,
      usdc_wallet: usdcWallet || null,
      currency: currency || null,
      payment_method: paymentMethod || null,
      iban: iban || null,
      paypal: paypal || null,
      monthly_report: monthlyReport,
      broadcast_enabled: broadcastEnabled,
    }

    try {
      await updateContact.mutateAsync({ id: contact.id, data })
      onClose()
    } catch {
      // Fehler wird im UI via isError sichtbar
    }
  }

  return (
    <form onSubmit={(e) => { void handleSubmit(e) }}>
      <div className="modal-body" style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 130px)' }}>

        {/* --- Stammdaten --- */}
        <div className="form-section">
          <h3 className="form-section-title">{t('editModal.sections.masterdata')}</h3>

          <div className="form-group">
            <label htmlFor="edit-contact-name">
              {t('editModal.fields.name')} <span aria-hidden="true">*</span>
            </label>
            <input
              id="edit-contact-name"
              className={`input${errors.name ? ' is-error' : ''}`}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-required="true"
              data-testid="edit-contact-name-input"
            />
            {errors.name && (
              <span className="field-error" role="alert">
                {errors.name}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-email">{t('editModal.fields.email')}</label>
            <input
              id="edit-contact-email"
              className={`input${errors.email ? ' is-error' : ''}`}
              type="text"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="edit-contact-email-input"
            />
            {errors.email && (
              <span className="field-error" role="alert">
                {errors.email}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-phone">{t('editModal.fields.phone')}</label>
            <input
              id="edit-contact-phone"
              className="input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t('editModal.placeholders.phone')}
              data-testid="edit-contact-phone-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-address">{t('editModal.fields.address')}</label>
            <input
              id="edit-contact-address"
              className="input"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={t('editModal.placeholders.address')}
              data-testid="edit-contact-address-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-notes">{t('editModal.fields.notes')}</label>
            <textarea
              id="edit-contact-notes"
              className="input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t('editModal.placeholders.notes')}
              data-testid="edit-contact-notes-input"
            />
          </div>
        </div>

        <hr className="form-divider" />

        {/* --- Wallets --- */}
        <div className="form-section">
          <h3 className="form-section-title">{t('editModal.sections.wallets')}</h3>

          <div className="form-group">
            <label htmlFor="edit-contact-helium-wallet">{t('editModal.fields.heliumWallet')}</label>
            <input
              id="edit-contact-helium-wallet"
              className="input"
              type="text"
              value={heliumWallet}
              onChange={(e) => setHeliumWallet(e.target.value)}
              placeholder={t('editModal.placeholders.heliumWallet')}
              data-testid="edit-contact-wallet-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-btc-wallet">{t('editModal.fields.btcWallet')}</label>
            <input
              id="edit-contact-btc-wallet"
              className="input"
              type="text"
              value={btcWallet}
              onChange={(e) => setBtcWallet(e.target.value)}
              placeholder={t('editModal.placeholders.btcWallet')}
              data-testid="edit-contact-btc-wallet-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-eth-wallet">{t('editModal.fields.ethWallet')}</label>
            <input
              id="edit-contact-eth-wallet"
              className="input"
              type="text"
              value={ethWallet}
              onChange={(e) => setEthWallet(e.target.value)}
              placeholder={t('editModal.placeholders.ethWallet')}
              data-testid="edit-contact-eth-wallet-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-usdt-wallet">{t('editModal.fields.usdtWallet')}</label>
            <input
              id="edit-contact-usdt-wallet"
              className="input"
              type="text"
              value={usdtWallet}
              onChange={(e) => setUsdtWallet(e.target.value)}
              data-testid="edit-contact-usdt-wallet-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-usdc-wallet">{t('editModal.fields.usdcWallet')}</label>
            <input
              id="edit-contact-usdc-wallet"
              className="input"
              type="text"
              value={usdcWallet}
              onChange={(e) => setUsdcWallet(e.target.value)}
              data-testid="edit-contact-usdc-wallet-input"
            />
          </div>
        </div>

        <hr className="form-divider" />

        {/* --- Zahlung --- */}
        <div className="form-section">
          <h3 className="form-section-title">{t('editModal.sections.payment')}</h3>

          <div className="form-group">
            <label htmlFor="edit-contact-currency">{t('editModal.fields.currency')}</label>
            <select
              id="edit-contact-currency"
              className="input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              data-testid="edit-contact-currency-select"
            >
              <option value="">{t('editModal.currencyPlaceholder')}</option>
              <option value="eur">EUR</option>
              <option value="usd">USD</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-payment-method">{t('editModal.fields.paymentMethod')}</label>
            <select
              id="edit-contact-payment-method"
              className="input"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              data-testid="edit-contact-payment-method-select"
            >
              <option value="">{t('editModal.paymentMethodPlaceholder')}</option>
              <option value="bank_transfer">{t('editModal.paymentMethods.bank_transfer')}</option>
              <option value="paypal">{t('editModal.paymentMethods.paypal')}</option>
              <option value="crypto">{t('editModal.paymentMethods.crypto')}</option>
              <option value="cryptoiot">{t('editModal.paymentMethods.cryptoiot')}</option>
              <option value="cash">{t('editModal.paymentMethods.cash')}</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-iban">{t('editModal.fields.iban')}</label>
            <input
              id="edit-contact-iban"
              className="input"
              type="text"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder={t('editModal.placeholders.iban')}
              data-testid="edit-contact-iban-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-contact-paypal">{t('editModal.fields.paypal')}</label>
            <input
              id="edit-contact-paypal"
              className="input"
              type="text"
              inputMode="email"
              value={paypal}
              onChange={(e) => setPaypal(e.target.value)}
              placeholder={t('editModal.placeholders.paypal')}
              data-testid="edit-contact-paypal-input"
            />
          </div>
        </div>

        <hr className="form-divider" />

        {/* --- Optionen --- */}
        <div className="form-section">
          <h3 className="form-section-title">{t('editModal.sections.options')}</h3>

          <div className="form-group form-group--inline">
            <label htmlFor="edit-contact-monthly-report">{t('editModal.fields.monthlyReport')}</label>
            <input
              id="edit-contact-monthly-report"
              type="checkbox"
              checked={monthlyReport}
              onChange={(e) => setMonthlyReport(e.target.checked)}
              data-testid="edit-contact-monthly-report-toggle"
            />
          </div>

          <div className="form-group form-group--inline">
            <label htmlFor="edit-contact-broadcast-enabled">{t('editModal.fields.broadcastEnabled')}</label>
            <input
              id="edit-contact-broadcast-enabled"
              type="checkbox"
              checked={broadcastEnabled}
              onChange={(e) => setBroadcastEnabled(e.target.checked)}
              data-testid="edit-contact-broadcast-enabled-toggle"
            />
          </div>

        </div>

        {updateContact.isError && (
          <div className="field-error" role="alert">
            {t('editModal.saveError')}
          </div>
        )}
      </div>

      <div className="modal-footer" style={{ position: 'sticky', bottom: 0 }}>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onClose}
          disabled={updateContact.isPending}
        >
          {t('editModal.cancelBtn')}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={updateContact.isPending}
          data-testid="edit-contact-submit"
        >
          {updateContact.isPending ? t('editModal.submitPending') : t('editModal.submitBtn')}
        </button>
      </div>
    </form>
  )
}

// --- Wrapper-Komponente ---

export function EditContactModal({ contact, isOpen, onClose }: Props) {
  const { t } = useTranslation('contacts')
  if (!isOpen) return null

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-contact-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      data-testid="edit-contact-modal"
      style={{ alignItems: 'flex-start', paddingTop: 32 }}
    >
      <div className="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 id="edit-contact-title">{t('editModal.title')}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('editModal.closeAriaLabel')}
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        {/* key=contact.id sorgt fuer automatischen State-Reset bei neuem Contact */}
        <EditContactForm key={contact.id} contact={contact} onClose={onClose} />
      </div>
    </div>
  )
}
